import React from 'react';
import {
    EyeOutlined,
    EditTwoTone,
    DeleteTwoTone,
    CopyTwoTone,
    CheckOutlined,
    CheckCircleOutlined,
    CloseOutlined,
    DownloadOutlined,
    ExportOutlined,
    SendOutlined,
    PrinterOutlined,
    ShareAltOutlined,
    HistoryOutlined,
    LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons';

/**
 * Menu Item Preset Configurations for ActionMenu
 *
 * These presets provide consistent menu item configurations across the application.
 * They are optional helpers - developers can still create custom configurations.
 *
 * Usage:
 * import { MENU_ITEM_PRESETS, getMenuPreset } from '@/design-system/constants/menuPresets';
 *
 * // Using preset key strings
 * <ActionMenu items={['view', 'edit', 'delete']} onAction={handleAction} />
 *
 * // Using preset objects directly
 * <ActionMenu
 *   items={[
 *     { ...MENU_ITEM_PRESETS.VIEW, onClick: handleView },
 *     { ...MENU_ITEM_PRESETS.EDIT, onClick: handleEdit },
 *   ]}
 * />
 */

export const MENU_ITEM_PRESETS = {
    // View/Read Actions
    VIEW: {
        key: 'view',
        label: 'View',
        icon: <EyeOutlined style={{ color: '#4A4A4A' }} />,
    },

    // Edit/Update Actions
    EDIT: {
        key: 'edit',
        label: 'Edit',
        icon: <EditTwoTone twoToneColor="#FED230" />,
    },

    // Duplicate/Copy Actions
    DUPLICATE: {
        key: 'duplicate',
        label: 'Duplicate',
        icon: <CopyTwoTone twoToneColor="#418FD7" />,
    },

    // Delete Actions
    DELETE: {
        key: 'delete',
        label: 'Delete',
        icon: <DeleteTwoTone twoToneColor="#FD3635" />,
        danger: true,
    },

    // Approval Actions
    APPROVE: {
        key: 'approve',
        label: 'Approve',
        icon: <CheckOutlined style={{ color: '#B3D741' }} />,
        color: '#B3D741',
    },

    REJECT: {
        key: 'reject',
        label: 'Reject',
        icon: <CloseOutlined style={{ color: '#FD3635' }} />,
        danger: true,
    },

    QUALIFY: {
        key: 'qualify',
        label: 'Instant Qualify',
        icon: <CheckCircleOutlined style={{ color: '#4CAF50' }} />,
        color: '#4CAF50',
    },

    // Export/Download Actions
    DOWNLOAD: {
        key: 'download',
        label: 'Download',
        icon: <DownloadOutlined style={{ color: '#4CAF50' }} />,
        color: '#4CAF50',
    },

    EXPORT: {
        key: 'export',
        label: 'Export',
        icon: <ExportOutlined style={{ color: '#4CAF50' }} />,
        color: '#4CAF50',
    },

    PRINT: {
        key: 'print',
        label: 'Print',
        icon: <PrinterOutlined style={{ color: '#4A4A4A' }} />,
    },

    // Communication Actions
    SEND: {
        key: 'send',
        label: 'Send',
        icon: <SendOutlined style={{ color: '#418FD7' }} />,
        color: '#418FD7',
    },

    SHARE: {
        key: 'share',
        label: 'Share',
        icon: <ShareAltOutlined style={{ color: '#418FD7' }} />,
        color: '#418FD7',
    },

    // History/Audit Actions
    HISTORY: {
        key: 'history',
        label: 'View History',
        icon: <HistoryOutlined style={{ color: '#4A4A4A' }} />,
    },

    // Lock/Unlock Actions
    LOCK: {
        key: 'lock',
        label: 'Lock',
        icon: <LockOutlined style={{ color: '#FF9800' }} />,
        color: '#FF9800',
    },

    UNLOCK: {
        key: 'unlock',
        label: 'Unlock',
        icon: <UnlockOutlined style={{ color: '#4CAF50' }} />,
        color: '#4CAF50',
    },
};

/**
 * Helper function to get a menu preset by key (case-insensitive)
 * @param {string} key - The preset key (e.g., 'view', 'edit', 'delete')
 * @returns {Object|null} The preset configuration or null if not found
 */
export const getMenuPreset = (key) => {
    if (!key || typeof key !== 'string') {
        return null;
    }
    return MENU_ITEM_PRESETS[key.toUpperCase()] || null;
};

/**
 * Common menu item combinations for quick setup
 *
 * Usage:
 * import { MENU_COMBINATIONS } from '@/design-system/constants/menuPresets';
 *
 * <ActionMenu items={MENU_COMBINATIONS.CRUD} onAction={handleAction} />
 */
export const MENU_COMBINATIONS = {
    // Basic CRUD operations
    CRUD: ['view', 'edit', 'delete'],

    // Full CRUD with duplicate
    CRUD_FULL: ['view', 'edit', 'duplicate', 'delete'],

    // View and Edit only
    VIEW_EDIT: ['view', 'edit'],

    // Approval workflow
    APPROVAL: ['approve', 'reject'],

    // Export options
    EXPORT_OPTIONS: ['download', 'export', 'print'],
};

/**
 * Standard menu item colors for consistency
 */
export const MENU_ITEM_COLORS = {
    DEFAULT: '#4A4A4A',      // Gray - for standard actions
    PRIMARY: '#FED230',      // Yellow - brand color (edit)
    DANGER: '#FD3635',       // Red - for destructive actions
    SUCCESS: '#B3D741',      // Green - for positive actions (approve)
    INFO: '#418FD7',         // Blue - for info actions (duplicate, share)
    WARNING: '#FF9800',      // Orange - for warning actions (lock)
    DOWNLOAD: '#4CAF50',     // Green - for download/export actions
};

export default MENU_ITEM_PRESETS;
